import { useEffect, useRef, useState } from "react";

export default function ContactForm() {
  const ref = useRef<HTMLElement>(null);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".contact-el").forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = "1";
                (el as HTMLElement).style.transform = "translateY(0)";
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });
    setSending(true);
    setError("");
    try {
      const res = await fetch("/mail.php", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      const json = await res.json();
      if (res.ok && json.success) {
        setSubmitted(true);
      } else {
        setError(json.message || "送信に失敗しました。時間をおいて再度お試しください。");
      }
    } catch {
      setError("通信エラーが発生しました。時間をおいて再度お試しください。");
    } finally {
      setSending(false);
    }
  };

  const inputClass = "w-full bg-navy-light text-white text-sm rounded-lg px-4 py-3 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-inexx-blue transition-all duration-200";
  const inputStyle = { border: "1px solid rgba(0,119,204,0.25)" };

  return (
    <section id="contact" ref={ref} className="py-24 bg-navy relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px section-divider" />
      <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-inexx-blue/5 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-80 h-80 rounded-full bg-inexx-blue/5 blur-3xl pointer-events-none" />

      <div className="max-w-3xl mx-auto px-4 md:px-8">
        {/* Section Title */}
        <div className="text-center mb-14">
          <p className="contact-el text-inexx-blue text-sm font-semibold tracking-widest uppercase mb-3 transition-all duration-500" style={{ opacity: 0, transform: "translateY(20px)" }}>
            Contact Us
          </p>
          <h2 className="contact-el text-3xl md:text-4xl font-bold text-white transition-all duration-500" style={{ opacity: 0, transform: "translateY(20px)" }}>
            お問い合わせ
          </h2>
          <p className="contact-el text-gray-400 mt-4 text-sm transition-all duration-500" style={{ opacity: 0, transform: "translateY(20px)" }}>
            ご質問・ご相談はお気軽にお問い合わせください。担当者より折り返しご連絡いたします。
          </p>
          <div className="contact-el mx-auto mt-4 w-16 h-0.5 bg-inexx-blue transition-all duration-500" style={{ opacity: 0 }} />
        </div>

        {/* Form Card */}
        <div
          className="contact-el rounded-2xl p-8 md:p-10 transition-all duration-500"
          style={{
            opacity: 0,
            transform: "translateY(30px)",
            background: "rgba(13,41,82,0.5)",
            border: "1px solid rgba(0,119,204,0.25)",
          }}
        >
          {submitted ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-inexx-blue/20 mx-auto mb-6"
                style={{ border: "2px solid rgba(0,119,204,0.5)" }}>
                <i className="ri-check-line text-3xl text-inexx-blue" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">送信完了！</h3>
              <p className="text-gray-400 text-sm">
                お問い合わせありがとうございます。<br />
                内容を確認の上、担当者よりご連絡いたします。
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              {error && (
                <div className="bg-red-500/20 border border-red-500/50 text-red-300 text-sm rounded-lg px-4 py-3">
                  {error}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-gray-300 text-xs font-medium mb-2 block">
                    お名前 <span className="text-inexx-blue">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="山田 太郎"
                    className={inputClass}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label className="text-gray-300 text-xs font-medium mb-2 block">
                    会社名
                  </label>
                  <input
                    type="text"
                    name="company"
                    placeholder="株式会社〇〇"
                    className={inputClass}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div>
                <label className="text-gray-300 text-xs font-medium mb-2 block">
                  メールアドレス <span className="text-inexx-blue">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="example@company.co.jp"
                  className={inputClass}
                  style={inputStyle}
                />
              </div>

              <div>
                <label className="text-gray-300 text-xs font-medium mb-2 block">
                  電話番号
                </label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="000-0000-0000"
                  className={inputClass}
                  style={inputStyle}
                />
              </div>

              <div>
                <label className="text-gray-300 text-xs font-medium mb-2 block">
                  お問い合わせ種別
                </label>
                <select
                  name="category"
                  className={inputClass + " cursor-pointer"}
                  style={{ ...inputStyle, appearance: "none" }}
                >
                  <option value="" className="bg-navy">選択してください</option>
                  <option value="service" className="bg-navy">業務内容について</option>
                  <option value="estimate" className="bg-navy">見積もり依頼</option>
                  <option value="career" className="bg-navy">採用情報について</option>
                  <option value="other" className="bg-navy">その他</option>
                </select>
              </div>

              <div>
                <label className="text-gray-300 text-xs font-medium mb-2 flex justify-between">
                  <span>お問い合わせ内容 <span className="text-inexx-blue">*</span></span>
                  <span className={charCount > 500 ? "text-red-400" : "text-gray-500"}>{charCount}/500</span>
                </label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="お問い合わせ内容をご記入ください。"
                  className={inputClass + " resize-none"}
                  style={inputStyle}
                  onChange={(e) => setCharCount(e.target.value.length)}
                />
                {charCount > 500 && (
                  <p className="text-red-400 text-xs mt-1">500文字以内でご入力ください。</p>
                )}
              </div>

              <button
                type="submit"
                disabled={sending || charCount > 500}
                className="w-full cursor-pointer whitespace-nowrap bg-inexx-blue hover:bg-inexx-blue-dark disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-lg text-sm transition-all duration-200 hover:scale-[1.02] flex items-center justify-center gap-2 mt-2"
              >
                {sending ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    送信中...
                  </>
                ) : (
                  <>
                    <i className="ri-mail-send-line" />
                    送信する
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
